import { type TabItem, Tabs } from "@kyan/ui";

import { usePositionsDrawerOpenOrdersTab } from "#/store/app/positionsDrawer/positionsDrawerOpenOrdersTab";
import { usePositionsDrawerPositionsTab } from "#/store/app/positionsDrawer/positionsDrawerPositionsTab";
import type { PositionsDrawerRfqTab } from "#/store/app/positionsDrawer/positionsDrawerRfqTab";
import { usePositionsDrawerRfqTab } from "#/store/app/positionsDrawer/positionsDrawerRfqTab";
import { usePositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";
import { isPerpsRfq, useRfqResponses } from "#/store/rfq/rfqResponses";
import { useOpenOrders } from "#/store/wallet/account/openOrders";
import { usePositions } from "#/store/wallet/account/positions";
import { appendCount } from "#/utils/appendCount";

import { MarketSwitch } from "./components/MarketSwitch";

const getTabs = ({
  perpsCount,
  optionsCount,
}: {
  perpsCount: number;
  optionsCount: number;
}): TabItem[] => [
  {
    id: "all",
    label: appendCount("All", optionsCount + perpsCount),
  },
  {
    id: "options",
    label: appendCount("Options", optionsCount),
  },
  {
    id: "perps",
    label: appendCount("Perps", perpsCount),
  },
];

export const Header: React.FC = () => {
  const { positionsDrawerTab } = usePositionsDrawerTab();
  const { positionsDrawerPositionsTab, setPositionsDrawerPositionsTab } =
    usePositionsDrawerPositionsTab();
  const { positionsDrawerOpenOrdersTab, setPositionsDrawerOpenOrdersTab } =
    usePositionsDrawerOpenOrdersTab();
  const { positionsDrawerRfqTab, setPositionsDrawerRfqTab } =
    usePositionsDrawerRfqTab();

  const { positions } = usePositions();
  const { openOrders } = useOpenOrders();
  const { marketResponses: rfqResponses } = useRfqResponses();

  const positionsPerpCount = positions.filter(
    (position) => position.instrumentType === "perps",
  ).length;
  const positionOptionCount = positions.length - positionsPerpCount;
  const openOrdersPerpsCount = openOrders.filter(
    (order) => !order.isOption,
  ).length;
  const openOrdersOptionsCount = openOrders.length - openOrdersPerpsCount;

  const rfqPerpCount = rfqResponses.filter(isPerpsRfq).length;
  const rfqOptionCount = rfqResponses.length - rfqPerpCount;

  const [perpsCount, optionsCount] = {
    positions: [positionsPerpCount, positionOptionCount],
    "open-orders": [openOrdersPerpsCount, openOrdersOptionsCount],
    rfq: [rfqPerpCount, rfqOptionCount],
  }[positionsDrawerTab];

  const tabs = getTabs({
    perpsCount,
    optionsCount,
  });

  const { value, onChange } = {
    positions: {
      value: positionsDrawerPositionsTab,
      onChange: setPositionsDrawerPositionsTab,
    },
    "open-orders": {
      value: positionsDrawerOpenOrdersTab,
      onChange: setPositionsDrawerOpenOrdersTab,
    },
    rfq: { value: positionsDrawerRfqTab, onChange: setPositionsDrawerRfqTab },
  }[positionsDrawerTab];

  return (
    <div className="border-divider flex h-10 w-full items-center gap-8 border-b px-10">
      <MarketSwitch />

      <div className="bg-divider h-2.5 w-px" />

      <Tabs
        items={tabs}
        value={value}
        variant="primary"
        itemClassName="h-10.25 text-xs"
        onChange={(id) => {
          onChange(id as PositionsDrawerRfqTab);
        }}
      />
    </div>
  );
};
